import type {ConfiguratorVariant} from "#pricing/types/pricing-types"
import {configuratorVariantSchema} from "#pricing/types/pricing-types"
import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition, ParameterValue} from "@cm/graph-v3"
import {parameterValue} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type ConfigVariantField = "groupId" | "groupName" | "variantId" | "variantName"
export type ConfigVariantOperator = "equals" | "contains" | "notContains"

export type ConfigVariantRule = {
    field: ConfigVariantField
    operator: ConfigVariantOperator
    value: string
    caseInsensitive?: boolean
}

export function evaluateRule(rule: ConfigVariantRule, variant: ConfiguratorVariant): boolean {
    let actual = variant[rule.field]
    let expected = rule.value
    if (rule.caseInsensitive) {
        actual = actual.toLowerCase()
        expected = expected.toLowerCase()
    }
    switch (rule.operator) {
        case "equals":
            return actual === expected
        case "contains":
            return actual.includes(expected)
        case "notContains":
            return !actual.includes(expected)
    }
}

export type ConfigVariantConditionParameters = {
    variant: ParameterValue<ConfiguratorVariant, PricingContext>
    rules: ConfigVariantRule[]
}

const configVariantRuleSchema = z.object({
    field: z.union([z.literal("groupId"), z.literal("groupName"), z.literal("variantId"), z.literal("variantName")]),
    operator: z.union([z.literal("equals"), z.literal("contains"), z.literal("notContains")]),
    value: z.string(),
    caseInsensitive: z.optional(z.boolean()),
})

const configVariantConditionDefinition = {
    nodeClass: "ConfigVariantCondition",
    validation: {
        parameters: z.object({
            variant: parameterValue(configuratorVariantSchema),
            rules: z.array(configVariantRuleSchema),
        }),
        returns: z.boolean(),
    },
} satisfies GraphNodeDefinition<boolean, ConfigVariantConditionParameters>

@registerPricingGraphNode
export class ConfigVariantCondition extends PricingNode<boolean, ConfigVariantConditionParameters> {
    public override get definition() {
        return configVariantConditionDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        if (this.parameters.rules.length === 0) return false
        const variant = await evaluator.get(this.parameters.variant)
        return this.parameters.rules.every((rule) => evaluateRule(rule, variant))
    }
}
